import { useEditor } from '@tiptap/react';
import { useEffect } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';

/**
 * Custom hook for Tiptap editor functionality
 * @param {string} value - Initial content for the editor
 * @param {function} onChange - Callback function when content changes
 * @returns {object} Editor instance and toolbar action handlers
 */
const useTiptapEditor = (value, onChange) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value) {
      if (editor.getHTML() !== value) {
        editor.commands.setContent(value);
      }
    }
  }, [editor, value]);

  // Toolbar action handlers
  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();
  const toggleUnderline = () => editor.chain().focus().toggleUnderline().run();
  const setParagraph = () => editor.chain().focus().setParagraph().run();
  const toggleHeading = (level) => editor.chain().focus().toggleHeading({ level }).run();
  const toggleBulletList = () => editor.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () => editor.chain().focus().toggleOrderedList().run();
  const setTextAlign = (align) => editor.chain().focus().setTextAlign(align).run();

  // Check active states
  const isActive = {
    bold: editor?.isActive('bold'),
    italic: editor?.isActive('italic'),
    underline: editor?.isActive('underline'),
    paragraph: editor?.isActive('paragraph'),
    heading1: editor?.isActive('heading', { level: 1 }),
    heading2: editor?.isActive('heading', { level: 2 }),
    heading3: editor?.isActive('heading', { level: 3 }),
    bulletList: editor?.isActive('bulletList'),
    orderedList: editor?.isActive('orderedList'),
    alignLeft: editor?.isActive({ textAlign: 'left' }),
    alignCenter: editor?.isActive({ textAlign: 'center' }),
    alignRight: editor?.isActive({ textAlign: 'right' }),
  };

  return {
    editor,
    isActive,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    setParagraph,
    toggleHeading,
    toggleBulletList,
    toggleOrderedList,
    setTextAlign,
  };
};

export default useTiptapEditor;
